import { Component } from '@theme/component';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const CLE_PAUSE = 'tretto-bandeau-pause';
const MARGE_REVELATION = 24;

/**
 * TRETTO - <tretto-bandeau-defilant>
 *
 * Bandeau d'annonces qui défile en continu (sections/tretto-bandeau-defilant.liquid).
 *
 * - Duplique le groupe de messages autant de fois que nécessaire pour couvrir la largeur
 *   de l'écran, puis anime la piste d'exactement une largeur de groupe : la boucle est
 *   parfaite, sans à-coup. Les copies sont `aria-hidden="true"` et leurs liens en
 *   `tabindex="-1"` : ignorées par les lecteurs d'écran et le clavier, mais cliquables à la
 *   souris et au doigt (une copie occupe souvent l'écran à la place du groupe d'origine).
 * - La durée est calculée à partir d'une vitesse en pixels par seconde (data-vitesse), pour
 *   garder la même vitesse de lecture quels que soient l'écran et la longueur des messages.
 * - Pause : survol et focus clavier (CSS), bouton pause / lecture (mémorisé pour la session,
 *   et réappliqué avant le premier affichage par un script en ligne du Liquid), sélection
 *   d'un bloc dans l'éditeur de thème.
 * - `prefers-reduced-motion: reduce` ou pause : mode statique, aucune copie, rangée que l'on
 *   fait glisser (la fenêtre devient focusable pour le défilement au clavier, et un fondu
 *   sur le bord signale qu'il reste des messages cachés : attributs data-avant / data-apres).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - La fenêtre visible (rognée).
 * @property {HTMLElement} track - La piste animée, qui contient le groupe et ses copies.
 * @property {HTMLElement} group - Le groupe de messages d'origine.
 * @property {HTMLButtonElement} [pauseButton] - Le bouton pause / lecture.
 *
 * @extends {Component<Refs>}
 */
class TrettoBandeauDefilant extends Component {
  requiredRefs = ['viewport', 'track', 'group'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** Largeur de la fenêtre lors de la dernière construction. */
  #largeurFenetre = 0;

  /** Largeur du groupe d'origine lors de la dernière construction. */
  #largeurGroupe = 0;

  connectedCallback() {
    super.connectedCallback();

    // Le script en ligne du Liquid a normalement déjà posé l'attribut avant le premier
    // affichage ; on le repose ici au cas où (section re-rendue dans l'éditeur de thème).
    // Sans bouton, la pause ne pourrait pas être levée : on ne la réapplique pas.
    if (this.refs.pauseButton && lirePauseMemorisee()) this.setAttribute('data-pause', '');

    this.#mettreAJourBouton();
    this.#construire(true);

    this.#resizeObserver = new ResizeObserver(() => this.#construire());
    this.#resizeObserver.observe(this.refs.viewport);
    this.#resizeObserver.observe(this.refs.group);

    reducedMotion.addEventListener('change', this.#surChangementPreference);
    this.refs.viewport.addEventListener('focusin', this.#surFocus);
    this.refs.viewport.addEventListener('scroll', this.#surDefilement, { passive: true });
    document.addEventListener('shopify:block:select', this.#surSelectionBloc);
    document.addEventListener('shopify:block:deselect', this.#surDeselectionBloc);

    this.setAttribute('data-pret', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    reducedMotion.removeEventListener('change', this.#surChangementPreference);
    this.refs.viewport?.removeEventListener('focusin', this.#surFocus);
    this.refs.viewport?.removeEventListener('scroll', this.#surDefilement);
    document.removeEventListener('shopify:block:select', this.#surSelectionBloc);
    document.removeEventListener('shopify:block:deselect', this.#surDeselectionBloc);
  }

  /**
   * Bouton pause / lecture (déclaré dans le Liquid avec on:click="/togglePause").
   */
  togglePause() {
    const enPause = !this.hasAttribute('data-pause');

    this.toggleAttribute('data-pause', enPause);
    memoriserPause(enPause);
    this.#mettreAJourBouton();
    this.#construire(true);

    if (enPause) this.refs.viewport.scrollLeft = 0;
  }

  /** @returns {boolean} Vrai quand le bandeau doit rester immobile. */
  get #estStatique() {
    return reducedMotion.matches || this.hasAttribute('data-pause');
  }

  #surChangementPreference = () => {
    this.#construire(true);
  };

  /**
   * Construit (ou reconstruit) les copies et les variables d'animation.
   * @param {boolean} [forcer] - Reconstruire même si les dimensions n'ont pas changé.
   */
  #construire(forcer = false) {
    const { viewport, track, group } = this.refs;
    if (!viewport || !track || !group) return;

    if (this.#estStatique) {
      this.#retirerCopies();
      this.#largeurFenetre = 0;
      this.#largeurGroupe = 0;
      this.#preparerFenetre();
      return;
    }

    this.#preparerFenetre();

    const largeurFenetre = viewport.clientWidth;
    const largeurGroupe = group.getBoundingClientRect().width;

    if (largeurFenetre === 0 || largeurGroupe === 0) return;

    const dimensionsInchangees =
      largeurFenetre === this.#largeurFenetre && Math.abs(largeurGroupe - this.#largeurGroupe) < 0.5;
    if (!forcer && dimensionsInchangees) return;

    this.#largeurFenetre = largeurFenetre;
    this.#largeurGroupe = largeurGroupe;

    // Piste = groupe d'origine + copies. Il faut au moins « fenêtre + un groupe » de contenu
    // pour qu'aucun vide n'apparaisse pendant la translation d'une largeur de groupe.
    const copiesNecessaires = Math.max(1, Math.ceil(largeurFenetre / largeurGroupe));
    const copies = Array.from(track.querySelectorAll(':scope > [data-tretto-copie]'));

    if (forcer) {
      for (const copie of copies) copie.remove();
      copies.length = 0;
    }

    for (let index = copies.length; index < copiesNecessaires; index++) {
      track.append(creerCopie(group));
    }

    for (let index = copies.length - 1; index >= copiesNecessaires; index--) {
      copies[index]?.remove();
    }

    const vitesse = Math.max(5, Number(this.dataset.vitesse) || 35);
    const duree = Math.max(4, largeurGroupe / vitesse);

    this.style.setProperty('--tretto-bandeau-distance', `${largeurGroupe}px`);
    this.style.setProperty('--tretto-bandeau-duree', `${duree.toFixed(2)}s`);
  }

  /**
   * En mode statique sur mobile, la fenêtre se fait glisser : on la rend focusable et nommée
   * pour pouvoir la faire défiler au clavier. Inutile quand tout tient à l'écran.
   */
  #preparerFenetre() {
    const { viewport } = this.refs;

    if (this.#estDefilable()) {
      viewport.setAttribute('tabindex', '0');
      viewport.setAttribute('role', 'group');
      viewport.setAttribute('aria-label', 'Annonces, faites défiler pour tout lire');
    } else {
      viewport.removeAttribute('tabindex');
      viewport.removeAttribute('role');
      viewport.removeAttribute('aria-label');
    }

    this.#mettreAJourBords();
  }

  /**
   * Vrai seulement en mode statique quand la fenêtre se fait réellement glisser (mobile :
   * overflow-x auto) et que tout ne tient pas à l'écran. Sur ordinateur, la fenêtre est
   * rognée sans défilement : elle ne doit pas devenir focusable ni afficher de fondu.
   * @returns {boolean}
   */
  #estDefilable() {
    const { viewport } = this.refs;
    if (!viewport || !this.#estStatique) return false;

    const { overflowX } = getComputedStyle(viewport);
    const peutDefiler = overflowX === 'auto' || overflowX === 'scroll';
    return peutDefiler && viewport.scrollWidth - viewport.clientWidth > 1;
  }

  #surDefilement = () => {
    this.#mettreAJourBords();
  };

  /**
   * Mode statique sur mobile : signale par un fondu (CSS) le bord derrière lequel il reste
   * des messages à faire glisser. `data-avant` : du contenu est caché à gauche ;
   * `data-apres` : du contenu est caché à droite.
   */
  #mettreAJourBords() {
    const { viewport } = this.refs;
    if (!viewport) return;

    const maximum = viewport.scrollWidth - viewport.clientWidth;
    const defilable = this.#estDefilable();
    const position = Math.abs(viewport.scrollLeft);

    viewport.toggleAttribute('data-avant', defilable && position > 1);
    viewport.toggleAttribute('data-apres', defilable && position < maximum - 1);
  }

  #retirerCopies() {
    for (const copie of this.refs.track.querySelectorAll(':scope > [data-tretto-copie]')) {
      copie.remove();
    }
  }

  #mettreAJourBouton() {
    const { pauseButton } = this.refs;
    if (!pauseButton) return;

    const enPause = this.hasAttribute('data-pause');
    const libelle = enPause ? pauseButton.dataset.labelReprise : pauseButton.dataset.labelPause;

    if (libelle) pauseButton.setAttribute('aria-label', libelle);
  }

  /**
   * Quand un lien reçoit le focus clavier, l'animation est mise en pause (CSS :focus-within).
   * On la cale alors pour que le lien soit visible dans la fenêtre.
   * Uniquement pour le focus clavier (:focus-visible) : un clic de souris donne aussi le focus
   * au lien (Chrome), et déplacer la piste entre l'appui et le relâchement du bouton ferait
   * atterrir le clic à côté du lien, qui ne s'ouvrirait pas.
   * @param {FocusEvent} event
   */
  #surFocus = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!estFocusClavier(event.target)) return;
    this.#reveler(event.target);
  };

  /**
   * Cale l'animation pour que l'élément soit visible au début de la fenêtre.
   * @param {Element} element
   */
  #reveler(element) {
    if (this.#estStatique) {
      element.closest('.tretto-bandeau__message')?.scrollIntoView({ block: 'nearest', inline: 'center' });
      return;
    }

    const message = element.closest('.tretto-bandeau__message');
    const { track, group } = this.refs;
    if (!(message instanceof HTMLElement) || !group.contains(message)) return;

    const animation = track.getAnimations()[0];
    const largeurGroupe = this.#largeurGroupe;
    if (!animation || largeurGroupe === 0) return;

    const duree = Number(animation.effect?.getComputedTiming().duration);
    if (!Number.isFinite(duree) || duree <= 0) return;

    // On laisse une petite marge pour que le message ne soit pas sous le fondu du bord gauche.
    const decalage = Math.max(0, message.offsetLeft - MARGE_REVELATION);
    const fraction = (decalage % largeurGroupe) / largeurGroupe;
    const sensInverse = getComputedStyle(track).animationDirection === 'reverse';

    // En sens inverse, t = (1 - fraction) × durée. Pour fraction = 0 (premier message), t
    // vaudrait exactement la durée : avec une animation infinie, c'est le début de l'itération
    // suivante, piste décalée d'une largeur de groupe et groupe d'origine (celui qui a le
    // focus) hors écran. On recule donc d'une milliseconde (moins d'un pixel).
    animation.currentTime = sensInverse ? Math.max(0, (1 - fraction) * duree - 1) : fraction * duree;
  }

  /** @param {Event} event */
  #surSelectionBloc = (event) => {
    if (!this.#concerneCetteSection(event)) return;

    this.setAttribute('data-editeur-pause', '');
    if (event.target instanceof Element) this.#reveler(event.target);
  };

  /** @param {Event} event */
  #surDeselectionBloc = (event) => {
    if (!this.#concerneCetteSection(event)) return;
    this.removeAttribute('data-editeur-pause');
  };

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #concerneCetteSection(event) {
    const detail = /** @type {CustomEvent<{ sectionId?: string }>} */ (event).detail;
    return Boolean(detail?.sectionId) && detail.sectionId === this.dataset.sectionId;
  }
}

/** Éléments qui peuvent recevoir le focus clavier (le texte enrichi peut contenir des liens). */
const SELECTEUR_FOCUSABLE = 'a[href], area[href], button, input, select, textarea, summary, iframe, [tabindex]';

/**
 * Copie décorative du groupe : masquée aux lecteurs d'écran, hors de l'ordre de tabulation,
 * sans attributs de l'éditeur de thème ni références de composant.
 * Pas d'`inert` : un élément inert ne reçoit plus aucun clic, or les liens des copies
 * doivent rester cliquables à la souris et au doigt.
 * @param {HTMLElement} group
 * @returns {HTMLElement}
 */
function creerCopie(group) {
  const copie = /** @type {HTMLElement} */ (group.cloneNode(true));

  for (const element of [copie, ...copie.querySelectorAll('*')]) {
    element.removeAttribute('ref');
    element.removeAttribute('id');

    for (const attribut of Array.from(element.attributes)) {
      if (attribut.name.startsWith('data-shopify-editor')) element.removeAttribute(attribut.name);
    }
  }

  copie.setAttribute('aria-hidden', 'true');
  copie.setAttribute('data-tretto-copie', '');

  for (const element of copie.querySelectorAll(SELECTEUR_FOCUSABLE)) {
    element.setAttribute('tabindex', '-1');
  }

  return copie;
}

/**
 * @param {Element} element
 * @returns {boolean} Vrai si le focus vient du clavier (ou si le navigateur ne sait pas le dire).
 */
function estFocusClavier(element) {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
}

/** @returns {boolean} */
function lirePauseMemorisee() {
  try {
    return window.sessionStorage.getItem(CLE_PAUSE) === '1';
  } catch {
    return false;
  }
}

/** @param {boolean} enPause */
function memoriserPause(enPause) {
  try {
    if (enPause) {
      window.sessionStorage.setItem(CLE_PAUSE, '1');
    } else {
      window.sessionStorage.removeItem(CLE_PAUSE);
    }
  } catch {
    // Stockage indisponible (navigation privée stricte) : la pause vaut pour la page en cours.
  }
}

if (!customElements.get('tretto-bandeau-defilant')) {
  customElements.define('tretto-bandeau-defilant', TrettoBandeauDefilant);
}
